import type { AudioCaptureOptions, VideoCaptureOptions } from 'livekit-client';
import { VideoPresets } from 'livekit-client';

/**
 * LiveKit trata `deviceId` string como `{ exact: id }`.
 * O valor literal `'default'` (e string vazia) falha em muitos mobiles / Safari
 * com "Invalid constraint" / OverconstrainedError — não existe device com esse id.
 *
 * Preferir omitir (browser escolhe) ou `{ ideal }` para IDs reais.
 */
export function isPlaceholderDeviceId(deviceId: unknown): boolean {
  if (deviceId == null) return true;
  if (typeof deviceId !== 'string') return false;
  const trimmed = deviceId.trim();
  return trimmed === '' || trimmed.toLowerCase() === 'default';
}

/**
 * Converte escolha de dispositivo do PreJoin/localStorage em constraint seguro.
 * - placeholder → `undefined` (LiveKit aplica `{ ideal: 'default' }` sozinho)
 * - id real → `{ ideal }` (evita exact que quebra com device desconectado)
 */
export function toSafeDeviceIdConstraint(
  deviceId: string | undefined | null,
): ConstrainDOMString | undefined {
  if (isPlaceholderDeviceId(deviceId)) {
    return undefined;
  }
  return { ideal: String(deviceId).trim() };
}

function browserSupportsVoiceIsolation(): boolean {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getSupportedConstraints) {
    return false;
  }
  const supported = navigator.mediaDevices.getSupportedConstraints() as MediaTrackSupportedConstraints & {
    voiceIsolation?: boolean;
  };
  return Boolean(supported.voiceIsolation);
}

/** Opções de vídeo sem deviceId problemático. */
export function sanitizeVideoCaptureOptions(
  options?: VideoCaptureOptions | null,
): VideoCaptureOptions | undefined {
  if (!options) return undefined;
  const { deviceId, ...rest } = options;
  const safeDeviceId = typeof deviceId === 'string' ? toSafeDeviceIdConstraint(deviceId) : deviceId;
  const next: VideoCaptureOptions = { ...rest };
  if (safeDeviceId !== undefined) {
    next.deviceId = safeDeviceId;
  }
  return next;
}

/** Opções de áudio sem deviceId problemático. */
export function sanitizeAudioCaptureOptions(
  options?: AudioCaptureOptions | null,
): AudioCaptureOptions | undefined {
  if (!options) return undefined;
  const { deviceId, ...rest } = options;
  const safeDeviceId = typeof deviceId === 'string' ? toSafeDeviceIdConstraint(deviceId) : deviceId;
  const next: AudioCaptureOptions = { ...rest };
  if (safeDeviceId !== undefined) {
    next.deviceId = safeDeviceId;
  }
  return next;
}

/**
 * Defaults de room para áudio.
 * `voiceIsolation: undefined` sobrescreve o default `true` do livekit-client via
 * Object.assign — evita "Invalid constraint" em Safari/WebViews sem suporte.
 */
export function buildSafeRoomAudioCaptureDefaults(
  partial?: AudioCaptureOptions,
): AudioCaptureOptions {
  const sanitized = sanitizeAudioCaptureOptions(partial) ?? {};
  const defaults: AudioCaptureOptions & { voiceIsolation?: boolean } = {
    noiseSuppression: true,
    echoCancellation: true,
    autoGainControl: true,
    ...sanitized,
    voiceIsolation: browserSupportsVoiceIsolation() ? true : undefined,
  };
  return defaults;
}

export function buildSafeRoomVideoCaptureDefaults(
  partial?: VideoCaptureOptions,
): VideoCaptureOptions {
  return sanitizeVideoCaptureOptions(partial) ?? {};
}

/** Fallbacks progressivos para getUserMedia / setCameraEnabled. */
export const CAMERA_CAPTURE_FALLBACKS: VideoCaptureOptions[] = [
  {},
  { resolution: VideoPresets.h540.resolution },
  { resolution: VideoPresets.h360.resolution },
  { resolution: VideoPresets.h180.resolution },
  // Último recurso: sobrescreve deviceId exact/stale com ideal default.
  { deviceId: { ideal: 'default' } },
];

export const MIC_CAPTURE_FALLBACKS: AudioCaptureOptions[] = [
  {},
  {
    noiseSuppression: true,
    echoCancellation: true,
    autoGainControl: true,
  },
  {
    echoCancellation: true,
  },
  { deviceId: { ideal: 'default' } },
];

export function isInvalidMediaConstraintError(error: unknown): boolean {
  if (error instanceof DOMException) {
    const name = error.name?.toLowerCase() ?? '';
    if (name === 'overconstrainederror' || name === 'typeerror') {
      return true;
    }
  }
  const msg = (error instanceof Error ? error.message : String(error)).toLowerCase();
  return /invalid constraint|overconstrained|constraint not satisfied|could not start video source|could not start audio source/i.test(
    msg,
  );
}
